import React from 'react';
import { BUILD_VERSION, BUILD_TIMESTAMP } from '../buildVersion';

/**
 * Global Error Boundary
 * 
 * Catches any unhandled React errors and shows a friendly fallback UI
 * instead of a blank screen. Includes diagnostic info for debugging.
 */
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Generate a unique error ID for tracking
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log to console for debugging
    console.error('🚨 GlobalErrorBoundary caught error:', error);
    console.error('Component stack:', errorInfo?.componentStack);
    
    // Try to report to backend (non-blocking)
    this.reportError(error, errorInfo);
  }

  async reportError(error, errorInfo) {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${BACKEND_URL}/api/debug/client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error?.message || 'Unknown error',
          stack: error?.stack,
          componentStack: errorInfo?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          buildVersion: BUILD_VERSION,
          timestamp: new Date().toISOString()
        })
      });
    } catch (e) {
      // Silently fail - don't make error reporting cause more errors
    }
  }

  handleReload = () => {
    // Clear potentially corrupted localStorage before reload
    try {
      // Only clear CRM-specific cache, not auth
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('crm_') || key.includes('filter') || key.includes('cache'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`[ErrorBoundary] Cleared ${keysToRemove.length} cached keys`);
    } catch (e) {
      // Ignore
    }
    window.location.reload();
  };

  handleCopyDiagnostics = () => {
    const { error, errorInfo, errorId } = this.state;
    const diagnostics = {
      errorId,
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      buildVersion: BUILD_VERSION,
      buildTimestamp: BUILD_TIMESTAMP,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      localStorage: {
        keys: Object.keys(localStorage).filter(k => k.startsWith('crm_')).length
      }
    };
    
    navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2))
      .then(() => alert('Diagnostic info copied to clipboard'))
      .catch(() => alert('Failed to copy - check console for details'));
    
    console.log('Diagnostics:', diagnostics);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '500px',
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: '600', 
              color: '#1e293b',
              marginBottom: '12px'
            }}>
              Une erreur s'est produite
            </h1>
            <p style={{ 
              color: '#64748b', 
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              L'application a rencontré une erreur inattendue.
              <br />
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                Référence: {this.state.errorId}
              </span>
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleReload}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Recharger
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Dashboard
              </button>
            </div>
            
            <button
              onClick={this.handleCopyDiagnostics}
              style={{
                marginTop: '24px',
                backgroundColor: 'transparent',
                color: '#94a3b8',
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📋 Copier les infos de diagnostic
            </button>
            
            <p style={{ 
              marginTop: '24px', 
              fontSize: '11px', 
              color: '#cbd5e1' 
            }}>
              Version: {BUILD_VERSION || 'unknown'}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
